import { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { DateTime } from "luxon";
import Button from "@/components/Button";
import DateSelect from "@/components/DateSelect";
import Icon from "@/components/Icon";
import { centsToDollarsFormatted } from "@/lib/currencyHelpers";
import pluralize from "@/lib/pluralize";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import type { RegistrationOrder, RegistrationOrderLineItem } from "../types";

export function defaultOrderDueDate(dueDate: string | null): Date {
  if (dueDate) {
    const parsed = DateTime.fromISO(dueDate);
    if (parsed.isValid) {
      return parsed.toJSDate();
    }
  }

  return DateTime.now().plus({ days: 60 }).toJSDate();
}

function prorationSupportingText(lineItem: RegistrationOrderLineItem): string | undefined {
  const { proration } = lineItem;
  if (!proration?.proratedNumberOfMonths || proration.proratedPrice == null) {
    return undefined;
  }

  const adjustedPrice = proration.proratedPrice - proration.originalPrice;

  return `Proration ${proration.proratedNumberOfMonths} ${pluralize(
    proration.proratedNumberOfMonths,
    "Month"
  )} Original/Adjustment: ${centsToDollarsFormatted(proration.originalPrice)}/${centsToDollarsFormatted(adjustedPrice)}`;
}

function ApplicationApprovedOrderTotal({ totalCents }: { totalCents: number }) {
  const { isCompactLayout } = useIsCompactLayout();

  return (
    <div
      className={classNames(
        "flex w-full items-center justify-between py-3 text-title-small-bold text-sys-brand-on-surface",
        !isCompactLayout && "border-t border-sys-brand-surface-container-highest"
      )}
      data-testid="application-approved-order-total"
    >
      <p>Order Total</p>
      <p>{centsToDollarsFormatted(totalCents)}</p>
    </div>
  );
}

export interface ApplicationApprovedOrderPanelProps {
  order: RegistrationOrder;
  label?: string;
  editButtonTestId: string;
  dueDate: Date;
  onDueDateChange: (dueDate: Date) => void;
}

export default function ApplicationApprovedOrderPanel({
  order,
  label,
  editButtonTestId,
  dueDate,
  onDueDateChange
}: ApplicationApprovedOrderPanelProps) {
  const dueDateFieldRef = useRef<HTMLDivElement>(null);
  const initialDueDate = defaultOrderDueDate(order.dueDate);
  const [dueDateFieldReady, setDueDateFieldReady] = useState(false);
  const [lineItemsExpanded, setLineItemsExpanded] = useState(false);

  const lineItemCount = order.lineItems.length;

  useEffect(() => {
    const mountTimer = window.setTimeout(() => setDueDateFieldReady(true), 300);

    return () => clearTimeout(mountTimer);
  }, []);

  const handleEditOrder = () => {
    window.open(`/billing/orders/edit-order/${order.id}`, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="flex flex-col gap-4" data-testid={`application-approved-order-panel-${order.id}`}>
      <div className="flex flex-col gap-2">
        <p className="text-title-medium-bold font-serif text-sys-brand-on-surface">
          Order: {order.description} for #{order.person.pgaId}
        </p>
        {label ? <p className="text-body-medium text-sys-brand-on-surface-variant">{label}</p> : null}

        <div ref={dueDateFieldRef} className="mt-3">
          {dueDateFieldReady ? (
            <DateSelect
              label="Order Due Date *"
              useModal
              value={{ startDate: dueDate, endDate: dueDate }}
              onChange={(range) => {
                if (!range || typeof range === "string" || !range.startDate) {
                  return;
                }

                onDueDateChange(new Date(range.startDate));
              }}
              useSingleDate
              showFooter={false}
              startFrom={initialDueDate}
              shortcuts={{
                next30Days: {
                  text: "+30 days",
                  period: {
                    start: DateTime.now().plus({ days: 30 }).toJSDate(),
                    end: DateTime.now().plus({ days: 30 }).toJSDate()
                  }
                },
                next60Days: {
                  text: "+60 days",
                  period: {
                    start: DateTime.now().plus({ days: 60 }).toJSDate(),
                    end: DateTime.now().plus({ days: 60 }).toJSDate()
                  }
                }
              }}
            />
          ) : (
            <div
              className="h-[56px] w-full rounded-[4px] border border-sys-brand-outline bg-sys-brand-surface"
              aria-hidden="true"
              data-testid="order-due-date-placeholder"
            />
          )}
        </div>

        {lineItemCount > 0 ? (
          <button
            type="button"
            className="flex w-full items-center gap-1 py-2 text-left text-label-large text-sys-brand-primary"
            aria-expanded={lineItemsExpanded}
            data-testid={`application-approved-order-line-items-toggle-${order.id}`}
            onClick={() => setLineItemsExpanded((expanded) => !expanded)}
          >
            {lineItemsExpanded ? "Hide Products" : `Show Products (${lineItemCount})`}
            <Icon name={lineItemsExpanded ? "expand_less" : "expand_more"} />
          </button>
        ) : null}

        {lineItemsExpanded ? (
          <div className="flex flex-col">
            {order.lineItems.map((lineItem, index) => {
              const prorationText = prorationSupportingText(lineItem);
              const isLastItem = index === order.lineItems.length - 1;

              return (
                <div
                  key={lineItem.id}
                  className={classNames(
                    "flex items-center gap-4 py-3",
                    !isLastItem && "border-b border-sys-brand-outline-variant"
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-title-small-bold text-sys-brand-on-surface">{lineItem.name}</p>
                    {prorationText ? (
                      <p className="text-body-medium text-sys-brand-on-surface-variant">{prorationText}</p>
                    ) : null}
                  </div>
                  <p className="shrink-0 text-title-small-bold text-sys-brand-on-surface">
                    {centsToDollarsFormatted(lineItem.priceCents)}
                  </p>
                </div>
              );
            })}
          </div>
        ) : null}

        <ApplicationApprovedOrderTotal totalCents={order.lineItemsTotal} />

        <div className="flex justify-start">
          <Button variant="outlined" icon="open_in_new" onClick={handleEditOrder} testId={editButtonTestId}>
            Edit Order
          </Button>
        </div>
      </div>
    </div>
  );
}
